// FILE: apps/admin/src/display-currency.tsx
// VERSION: 2.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Multi-currency display context, CoinGecko GRAM/USD header rate badge, and formatting utilities (RUB, GRAM, USD) for Admin Panel where GRAM is the native coin of The Open Network (TON).
//   SCOPE: DisplayCurrency, FxRates, formatDisplayAmount, DisplayCurrencyProvider, useDisplayCurrency, DisplayCurrencyToggle, GramUsdRateBadge.
//   DEPENDS: ./api.js, ./components/Icons.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   DisplayCurrency - Supported admin display currencies (RUB, GRAM, USD; legacy TON alias mapped to GRAM)
//   DisplayCurrencyProvider - React context provider fetching /admin/fx and persisting selected display currency
//   DisplayCurrencyToggle - Topbar segmented control for switching RUB / GRAM / USD
//   FxRates - Exchange rates structure for multi-currency conversion
//   GramUsdRateBadge - Topbar badge displaying the daily CoinGecko GRAM/USD conversion rate
//   formatDisplayAmount - Convert kopecks into formatted RUB, GRAM, or USD string
//   useDisplayCurrency - Hook returning active currency, FX rates, and money formatters
// END_MODULE_MAP

import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { api } from './api.js';
import { AdminIcon } from './components/Icons.js';

export type DisplayCurrency = 'RUB' | 'GRAM' | 'USD' | 'TON';

export interface FxRates {
  gramUsd?: number;
  tonUsd: number;
  tonRub: number;
  usdRub: number;
  gramRub: number;
  updatedAt?: string;
}

const DEFAULT_FX: FxRates = {
  gramUsd: 5.2,
  tonUsd: 5.2,
  tonRub: 500,
  usdRub: 96,
  gramRub: 500,
};

// START_CONTRACT: formatDisplayAmount
//   PURPOSE: Convert an integer amount in kopecks into the selected display currency (RUB, GRAM, USD) using live FX rates.
//   INPUTS: kopecks, currency, fx
//   OUTPUTS: Formatted currency string
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: formatDisplayAmount
export function formatDisplayAmount(
  kopecks: number,
  currency: DisplayCurrency = 'RUB',
  fx: FxRates = DEFAULT_FX,
): string {
  // START_BLOCK_FORMAT_DISPLAY_AMOUNT
  const rubVal = (Number.isFinite(kopecks) ? kopecks : 0) / 100;
  if (currency === 'RUB') {
    return `${rubVal.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00A0₽`;
  }
  if (currency === 'USD') {
    const usdVal = fx.usdRub > 0 ? rubVal / fx.usdRub : rubVal / 96;
    return `$${usdVal.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  // GRAM is the native cryptocurrency of The Open Network (rebranded from TON).
  const rateRub = fx.gramRub > 1 ? fx.gramRub : fx.tonRub > 0 ? fx.tonRub : 500;
  const gramVal = rubVal / rateRub;
  return `${gramVal.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}\u00A0GRAM`;
  // END_BLOCK_FORMAT_DISPLAY_AMOUNT
}

interface DisplayCurrencyContextValue {
  currency: DisplayCurrency;
  setCurrency: (next: DisplayCurrency) => void;
  fx: FxRates;
  money: (kopecks: number) => string;
  gram: (milliGram: number) => string;
}

const DisplayCurrencyContext = createContext<DisplayCurrencyContextValue>({
  currency: 'RUB',
  setCurrency: () => undefined,
  fx: DEFAULT_FX,
  money: (k) => formatDisplayAmount(k, 'RUB', DEFAULT_FX),
  gram: (m) => `${(m / 1000).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GRAM`,
});

const STORAGE_KEY = 'admin_display_currency';

// START_CONTRACT: DisplayCurrencyProvider
//   PURPOSE: Provide active DisplayCurrency and live FX rates to the Admin workspace.
//   INPUTS: children ReactNode
//   OUTPUTS: React element
//   SIDE_EFFECTS: Reads/writes localStorage and queries /admin/fx
//   LINKS: M-ADMIN
// END_CONTRACT: DisplayCurrencyProvider
export function DisplayCurrencyProvider({ children }: { children: ReactNode }) {
  // START_BLOCK_DISPLAY_CURRENCY_PROVIDER
  const [currency, setCurrencyState] = useState<DisplayCurrency>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'TON') {
        localStorage.setItem(STORAGE_KEY, 'GRAM');
        return 'GRAM';
      }
      if (saved === 'RUB' || saved === 'GRAM' || saved === 'USD') {
        return saved;
      }
    } catch {
      /* ignore */
    }
    return 'RUB';
  });

  const { data: fxData } = useQuery({
    queryKey: ['admin-fx'],
    queryFn: () => api.get<FxRates>('/admin/fx'),
    refetchInterval: 60_000,
  });

  const fx = fxData ?? DEFAULT_FX;

  const setCurrency = (next: DisplayCurrency) => {
    const normalized: DisplayCurrency = next === 'TON' ? 'GRAM' : next;
    setCurrencyState(normalized);
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      /* ignore */
    }
  };

  return (
    <DisplayCurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        fx,
        money: (kopecks: number) => formatDisplayAmount(kopecks, currency, fx),
        gram: (milliGram: number) =>
          `${(milliGram / 1000).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GRAM`,
      }}
    >
      {children}
    </DisplayCurrencyContext.Provider>
  );
  // END_BLOCK_DISPLAY_CURRENCY_PROVIDER
}

// START_CONTRACT: useDisplayCurrency
//   PURPOSE: Access DisplayCurrencyContext in Admin components.
//   INPUTS: none
//   OUTPUTS: DisplayCurrencyContextValue
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: useDisplayCurrency
export function useDisplayCurrency() {
  // START_BLOCK_USE_DISPLAY_CURRENCY
  return useContext(DisplayCurrencyContext);
  // END_BLOCK_USE_DISPLAY_CURRENCY
}

// START_CONTRACT: GramUsdRateBadge
//   PURPOSE: Render the daily CoinGecko GRAM/USD exchange rate pill in the Admin Panel topbar header.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: GramUsdRateBadge
export function GramUsdRateBadge() {
  // START_BLOCK_GRAM_USD_RATE_BADGE
  const { fx } = useDisplayCurrency();
  const gramUsd =
    typeof fx.gramUsd === 'number' && fx.gramUsd > 0
      ? fx.gramUsd
      : fx.tonUsd > 0
        ? fx.tonUsd
        : 5.2;
  const gramRub = fx.gramRub > 1 ? fx.gramRub : fx.tonRub;

  return (
    <div
      className="gram-rate-pill"
      title={`Курс CoinGecko (раз в сутки): 1 GRAM = $${gramUsd.toFixed(2)} (${gramRub.toFixed(2)} ₽)`}
    >
      <AdminIcon name="gram" size={15} />
      <span>
        1 GRAM <span className="rate-muted">=</span> ${gramUsd.toFixed(2)}
      </span>
    </div>
  );
  // END_BLOCK_GRAM_USD_RATE_BADGE
}

// START_CONTRACT: DisplayCurrencyToggle
//   PURPOSE: Render topbar segmented currency switcher (RUB / GRAM / USD).
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Updates active display currency
//   LINKS: M-ADMIN
// END_CONTRACT: DisplayCurrencyToggle
export function DisplayCurrencyToggle() {
  // START_BLOCK_DISPLAY_CURRENCY_TOGGLE
  const { currency, setCurrency, fx } = useDisplayCurrency();
  const options: Array<{ id: DisplayCurrency; label: ReactNode }> = [
    {
      id: 'RUB',
      label: (
        <>
          <span className="currency-sym">₽ </span>
          <span>RUB</span>
        </>
      ),
    },
    {
      id: 'GRAM',
      label: (
        <>
          <AdminIcon name="gram" size={13} />
          <span>GRAM</span>
        </>
      ),
    },
    {
      id: 'USD',
      label: (
        <>
          <span className="currency-sym">$ </span>
          <span>USD</span>
        </>
      ),
    },
  ];

  const gramRate = fx.gramRub > 1 ? fx.gramRub : fx.tonRub;

  return (
    <div className="currency-toggle" title={`1 GRAM ≈ ${gramRate} ₽ · $1 ≈ ${fx.usdRub} ₽`}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={'currency-pill' + (currency === o.id ? ' active' : '')}
          onClick={() => setCurrency(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
  // END_BLOCK_DISPLAY_CURRENCY_TOGGLE
}
